import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Time series data types
interface VolumeDataPoint {
  time: string;
  total: number;
  successful: number;
  failed: number;
}

interface SuccessRateDataPoint {
  time: string;
  rate: number;
}

interface DurationDataPoint {
  time: string;
  avg_ms: number;
  p95_ms: number;
}

interface UsersDataPoint {
  time: string;
  count: number;
}

interface TimeSeriesData {
  volume: VolumeDataPoint[];
  success_rate: SuccessRateDataPoint[];
  duration: DurationDataPoint[];
  users: UsersDataPoint[];
}

// System health data types
interface ModalityDistribution {
  modality: string;
  count: number;
}

interface TierDistribution {
  tier: number;
  count: number;
}

interface SystemHealthData {
  modality_distribution: ModalityDistribution[];
  tier_distribution: TierDistribution[];
  total_reports_24h: number;
  error_rate_24h: number;
}

type Interval = "hour" | "day" | "week";

interface UseAdminTimeSeriesResult {
  data: TimeSeriesData | null;
  loading: boolean;
  fetch: (start: Date, end: Date, interval: Interval) => Promise<void>;
}

interface UseAdminSystemHealthResult {
  data: SystemHealthData | null;
  loading: boolean;
  fetch: () => Promise<void>;
}

/**
 * Hook for fetching time series analytics data
 *
 * Note: This hook calls the `get_analytics_time_series` RPC which is defined
 * in radiologylive-speak. If the RPC doesn't exist yet, the hook will gracefully
 * return empty data with an appropriate error toast.
 */
export function useAdminTimeSeries(): UseAdminTimeSeriesResult {
  const [data, setData] = useState<TimeSeriesData | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (start: Date, end: Date, interval: Interval) => {
    setLoading(true);
    try {
      const { data: result, error } = await supabase.rpc('get_analytics_time_series', {
        p_start_date: start.toISOString(),
        p_end_date: end.toISOString(),
        p_interval: interval
      });

      if (error) {
        // If RPC doesn't exist, return empty data structure
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('get_analytics_time_series RPC not found - using empty data');
          setData({
            volume: [],
            success_rate: [],
            duration: [],
            users: []
          });
          return;
        }
        throw error;
      }

      // Parse the result - RPC returns JSONB
      const parsed = result as unknown as {
        success: boolean;
        data: TimeSeriesData;
        error?: string;
      };

      if (!parsed.success) {
        throw new Error(parsed.error || 'Failed to fetch time series data');
      }

      setData(parsed.data);
    } catch (err) {
      console.error('Failed to fetch time series data:', err);
      // Set empty data on error to prevent UI breakage
      setData({
        volume: [],
        success_rate: [],
        duration: [],
        users: []
      });
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    data,
    loading,
    fetch
  };
}

/**
 * Hook for fetching system health metrics
 *
 * Note: This hook calls the `get_system_health_24h` RPC which is defined
 * in radiologylive-speak. If the RPC doesn't exist yet, the hook will gracefully
 * return empty data with an appropriate error toast.
 */
export function useAdminSystemHealth(): UseAdminSystemHealthResult {
  const [data, setData] = useState<SystemHealthData | null>(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: result, error } = await supabase.rpc('get_system_health_24h');

      if (error) {
        // If RPC doesn't exist, return empty data structure
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('get_system_health_24h RPC not found - using empty data');
          setData({
            modality_distribution: [],
            tier_distribution: [],
            total_reports_24h: 0,
            error_rate_24h: 0
          });
          return;
        }
        throw error;
      }

      // Parse the result - RPC returns JSONB
      const parsed = result as unknown as {
        success: boolean;
        data: SystemHealthData;
        error?: string;
      };

      if (!parsed.success) {
        throw new Error(parsed.error || 'Failed to fetch system health data');
      }

      setData(parsed.data);
    } catch (err) {
      console.error('Failed to fetch system health data:', err);
      // Set empty data on error to prevent UI breakage
      setData({
        modality_distribution: [],
        tier_distribution: [],
        total_reports_24h: 0,
        error_rate_24h: 0
      });
      toast.error('Failed to load system health data');
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    data,
    loading,
    fetch
  };
}
